import { useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ENC } from "@/constants/testIds";
import { listCptCodes } from "@/lib/cptApi";
import { cptKey, toCptSnapshot } from "@/lib/noteCpt";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  return "Unable to search CPT terminology.";
}

function catalogToRow(item) {
  return toCptSnapshot(item);
}

export default function EncounterCptWorkspace({
  selectedItems = [],
  onAdd,
  onRemove,
  readOnly,
  noteContext,
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const selected = new Set((selectedItems || []).map((item) => cptKey(item)));

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listCptCodes({ q: debounced || undefined, active: true })
      .then((data) => {
        if (cancelled) return;
        setItems((data.items || []).map(catalogToRow).filter(Boolean));
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setError(apiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  return (
    <div
      data-testid={ENC.screenCptWorkspace}
      className="relative z-10 grid w-full shrink-0 grid-cols-1 gap-4"
    >
      <section data-testid={ENC.screenCpt} className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-2.5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-900">CPT</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Find a CPT code, then add it to {noteContext || "this Note"}. Search stays open so you
            can add more than one.
          </p>
        </div>

        <div data-testid={ENC.screenCptSearch} className="space-y-3 p-4">
          <div className="relative z-10">
            <Search className="pointer-events-none absolute left-3 top-1/2 z-0 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              type="search"
              name="cpt-lookup"
              autoComplete="off"
              spellCheck={false}
              data-testid={ENC.screenCptSearchInput}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onPointerDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder="Search CPT code or description…"
              className="relative z-10 h-10 bg-white pl-10 text-sm"
              disabled={readOnly}
            />
          </div>

          {error ? (
            <p data-testid={ENC.screenCptSearchError} className="py-6 text-center text-sm text-rose-600">
              {error}
            </p>
          ) : loading ? (
            <div
              data-testid={ENC.screenCptSearchLoading}
              className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching CPT…
            </div>
          ) : !items.length ? (
            <p data-testid={ENC.screenCptSearchEmpty} className="py-6 text-center text-sm text-slate-500">
              {debounced ? `No CPT codes match “${debounced}”.` : "No demo CPT codes are available."}
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <p className="mb-2 text-xs font-medium text-slate-500">
                {items.length} result{items.length === 1 ? "" : "s"}
              </p>
              <ul
                data-testid={ENC.screenCptResults}
                className="divide-y divide-slate-100 rounded-md border border-slate-100"
              >
                {items.map((row) => {
                  const added = selected.has(cptKey(row));
                  return (
                    <li key={cptKey(row)} className="flex items-center gap-2 px-3 py-1.5">
                      <span className="shrink-0 font-fhir text-sm font-medium text-teal-800">
                        {row.code}
                      </span>
                      <span className="min-w-0 flex-1 text-sm leading-snug text-slate-800">
                        {row.display}
                      </span>
                      {added ? (
                        <span
                          data-testid={`${ENC.screenCptResult}-${row.code}-added`}
                          className="w-14 shrink-0 text-right text-xs font-semibold text-emerald-700"
                        >
                          Added
                        </span>
                      ) : (
                        <button
                          type="button"
                          data-testid={`${ENC.screenCptResult}-${row.code}`}
                          disabled={readOnly}
                          onClick={() => onAdd?.(row)}
                          className="w-14 shrink-0 rounded-md bg-sky-50 py-0.5 text-center text-xs font-semibold text-sky-800 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Add
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </section>

      <section
        data-testid={ENC.screenSelectedCpt}
        className="overflow-hidden rounded-lg border border-slate-200 bg-white"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-900">
            Selected CPT
          </h3>
          <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">{selectedItems.length}</Badge>
        </div>

        {!selectedItems.length ? (
          <p className="px-4 py-6 text-center text-sm text-slate-500">
            No CPT codes are selected for this Note yet.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {selectedItems.map((row) => (
              <li
                key={cptKey(row)}
                data-testid={`${ENC.screenSelectedCptRow}-${row.code}`}
                className="flex items-center gap-2 px-4 py-2"
              >
                <span className="shrink-0 font-fhir text-sm font-medium text-teal-800">{row.code}</span>
                <span className="min-w-0 flex-1 text-sm leading-snug text-slate-800">{row.display}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-400 hover:text-rose-600"
                  disabled={readOnly}
                  aria-label={`Remove ${row.code}`}
                  data-testid={`${ENC.screenCptRemove}-${row.code}`}
                  onClick={() => onRemove?.(row.code, row.system)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
